/**
 * The umpire's report preview — read-only.
 *
 * Authority: `05-reporting-marking.md` §A5 (the responsibility matrix) and §B5a, plus
 * ruling §E on serialisation.
 *
 * **Why it is read-only here.** An umpire's report is drafted, owned and pushed by the
 * owning Umpires Manager (write path W3). The TD sees it — he must, he agrees the marks
 * against it — but he does not draft or push it from this screen. The one authoring
 * surface the TD keeps on an umpire row is the `TD Closing Remarks` composer, which is a
 * separate surface — the Report Studio at `/marking/:officialId/report`. Showing an editable
 * box here would imply an ownership the system does not grant.
 *
 * **The text is assembled server-side.** `assembledText` arrives already serialised by
 * `assembleReportText` — the TD's closing remarks at the top as `TD - …`, a blank line,
 * then the owning UM's narrative as `UM - …`. The prefix is added in exactly one place
 * (the API), never here, because `report.td_closing_remarks` stores the remarks raw.
 *
 * The character contract is §B6's: `LIMIT = 800`, `NEAR = 770`.
 */

import type { ReportBody } from "../../api/types.js";

const LIMIT = 800;
const NEAR = 770;

const STAGE_LABEL: Readonly<Record<number, string>> = {
  0: "Blank",
  1: "AI Drafted",
  2: "TD Drafted",
  3: "TMS Draft",
  4: "TMS Published",
};

export interface ReportPanelProps {
  report: ReportBody;
}

function counterClass(count: number): string {
  if (count > LIMIT) return "charcount over";
  if (count >= NEAR) return "charcount near";
  return "charcount";
}

function counterTitle(count: number): string {
  if (count > LIMIT) return `Over The TMS Limit — Trim ${count - LIMIT} Characters`;
  if (count >= NEAR) return `Approaching The TMS Limit — ${LIMIT - count} Characters Left`;
  return "Within The TMS Limit";
}

export function ReportPanel({ report }: ReportPanelProps): React.JSX.Element {
  const count = report.assembledText.length;
  const stage = STAGE_LABEL[report.stage] ?? "Blank";

  return (
    <section className="panel panel--d2" aria-labelledby="reportTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="reportTitle">
          Report
        </h3>
        <span className="panel__note">
          {report.ownedByUm
            ? `Owned By UM${report.ownerUmShortName === null ? "" : ` · ${report.ownerUmShortName}`}`
            : "Owned By The TD"}
        </span>
        <span className="spacer" />
        <span className="readonly-tag">Read-Only</span>
        <span className="statuspill statuspill--muted">
          <span className="sdot" />
          {stage}
        </span>
      </div>

      <div className="airep">
        <textarea
          className="aitext"
          aria-label="Assembled report text"
          data-testid="assembled-text"
          readOnly
          value={report.assembledText}
        />
        <div className="aibar">
          <span className={counterClass(count)} title={counterTitle(count)}>
            {count} / {LIMIT}
          </span>
          {report.ownedByUm ? (
            <span className="btnhint">
              The owning UM drafts and pushes this report; the TD writes the closing remarks
              that head it.
            </span>
          ) : (
            <span className="btnhint">
              The TD authors this report in full — no prefix convention applies.
            </span>
          )}
        </div>
        {report.assembledText === "" ? (
          <div className="hidenote">
            <b>Blank —</b> nothing has been written yet. `Blank` is a real state on the
            Reporting page, not an absence.
          </div>
        ) : null}
      </div>

      <div className="panel__foot">
        <b>How the two halves sit together —</b> for an umpire the TD&rsquo;s closing remarks
        head the text as <span className="num">TD -</span>, with the owning Umpires
        Manager&rsquo;s narrative beneath as <span className="num">UM -</span>. Those two
        prefixes are added when the report is put together, never typed and never stored.
      </div>
    </section>
  );
}
